import { ErrorState, LoadingState } from '@/components/ui/async-state';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { AppScreen, Card, MetaBadge } from '@/components/ui/scene';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { BusinessShellNav } from './BusinessShellNav';
import { WorkspaceAccessState } from './WorkspaceAccessState';
import { useAuth } from '@/lib/auth/auth';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { useCreateOrganizationInvitation } from './useCreateOrganizationInvitation';
import { useBusinessWorkspace } from './useBusinessWorkspace';
import { useOrganizationInvitations } from './useOrganizationInvitations';
import { useOrganizationMembers } from './useOrganizationMembers';
import { useRemoveOrganizationMember } from './useRemoveOrganizationMember';
import { useRevokeOrganizationInvitation } from './useRevokeOrganizationInvitation';
import { canManageTeamTarget, formatWorkspaceRole } from './workspace';
import { Redirect } from 'expo-router';
import { LockKeyholeIcon, Trash2Icon, UsersIcon } from 'lucide-react-native';
import * as React from 'react';
import { Alert, ScrollView, View } from 'react-native';

export function WorkspaceTeamScene({
  organizationID,
  showSectionNav = true,
}: {
  organizationID: string;
  showSectionNav?: boolean;
}) {
  const session = useAuth((state) => state.session);
  const {
    organizationQuery,
    activeVenue,
    viewerRole,
    canInvite,
    orgAccessEnabled,
  } = useBusinessWorkspace(organizationID);
  const membersQuery = useOrganizationMembers(organizationID, orgAccessEnabled);
  const createInvitation = useCreateOrganizationInvitation();
  const removeMember = useRemoveOrganizationMember();
  const revokeInvitation = useRevokeOrganizationInvitation();
  const invitationsQuery = useOrganizationInvitations(organizationID, canInvite);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState<'manager' | 'social' | 'staff'>('manager');

  const handleInviteSubmit = React.useCallback(() => {
    if (!organizationID || !inviteEmail.trim()) {
      return;
    }

    createInvitation.mutate(
      { organizationID, email: inviteEmail.trim(), role: inviteRole },
      {
        onSuccess: () => {
          setInviteEmail('');
        },
      }
    );
  }, [createInvitation, inviteEmail, inviteRole, organizationID]);

  const handleRemoveMember = React.useCallback(
    (userID: string, name: string, role: string) => {
      Alert.alert(
        'Remove teammate',
        `Remove ${name} from this team? ${formatWorkspaceRole(role)} access will be removed immediately.`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: removeMember.isPending ? 'Removing…' : 'Remove',
            style: 'destructive',
            onPress: () => {
              removeMember.mutate({ organizationID, userID });
            },
          },
        ]
      );
    },
    [organizationID, removeMember]
  );

  const handleRevokeInvitation = React.useCallback(
    (invitationID: string, email: string, role: string) => {
      Alert.alert('Revoke invite', `Revoke the pending ${formatWorkspaceRole(role)} invite for ${email}?`, [
        { text: 'Cancel', style: 'cancel' },
        {
          text: revokeInvitation.isPending ? 'Revoking…' : 'Revoke',
          style: 'destructive',
          onPress: () => {
            revokeInvitation.mutate({ organizationID, invitationID });
          },
        },
      ]);
    },
    [organizationID, revokeInvitation]
  );

  if (!session) {
    return (
      <Redirect
        href={{ pathname: '/sign-in', params: { next: `/workspaces/${organizationID}/team` } }}
      />
    );
  }

  if (!orgAccessEnabled) {
    return <Redirect href={'/workspaces' as never} />;
  }

  return (
    <AppScreen>
      <ScrollView
        className="flex-1"
        contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 16, paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>
        {organizationQuery.isPending ? <LoadingState label="Loading team access..." /> : null}

        {organizationQuery.isError ? (
          <ErrorState
            body={getAPIErrorRecoveryCopy(
              organizationQuery.error,
              'workspace',
              'Unable to load team access.'
            )}
            onAction={() => organizationQuery.refetch()}
          />
        ) : null}

        {organizationQuery.data ? (
          <>
            <BusinessShellNav
              organizationID={organizationID}
              organizationName={organizationQuery.data.name}
              viewerRole={organizationQuery.data.viewer_role}
              activeTab="team"
              activeVenue={activeVenue}
              showSectionNav={showSectionNav}
            />

            {!canInvite ? (
              <WorkspaceAccessState
                icon={LockKeyholeIcon}
                title="Team access is limited"
                body="Only owners and managers can manage teammates here. Your current role still works in the venue spaces you have been assigned."
              />
            ) : (
            <>
            <Card className="mt-4 gap-4" tone="featured">
              <View className="flex-row items-center gap-2">
                <Icon as={UsersIcon} size={16} color="white" />
                <Text className="text-lg font-semibold text-foreground">Members</Text>
              </View>

              {membersQuery.isPending ? <LoadingState label="Loading team..." /> : null}

              {membersQuery.isError ? (
                <ErrorState
                  body={getAPIErrorRecoveryCopy(
                    membersQuery.error,
                    'workspace',
                    'Unable to load members.'
                  )}
                  onAction={() => membersQuery.refetch()}
                />
              ) : null}

              {membersQuery.data?.map((member) => (
                <View
                  key={member.user_id}
                  className="rounded-[18px] border border-border bg-card/70 p-4">
                  <View className="flex-row items-start justify-between gap-3">
                    <View className="flex-1 gap-1">
                      <Text className="font-semibold text-foreground">
                        {member.display_name || member.username || member.email}
                      </Text>
                      <Text variant="small" className="text-muted-foreground">
                        {member.email}
                      </Text>
                    </View>
                    <MetaBadge label={formatWorkspaceRole(member.role)} />
                  </View>
                  {canManageTeamTarget(viewerRole, member.role) ? (
                    <Button
                      variant="ghost"
                      className="mt-3"
                      disabled={removeMember.isPending}
                      onPress={() =>
                        handleRemoveMember(
                          member.user_id,
                          member.display_name || member.username || member.email,
                          member.role
                        )
                      }>
                      <Icon as={Trash2Icon} size={16} color="white" />
                      <Text className="font-semibold">Remove teammate</Text>
                    </Button>
                  ) : null}
                </View>
              ))}

              {canInvite && invitationsQuery.data?.length ? (
                <View className="gap-3">
                  <Text className="font-semibold text-foreground">Pending invites</Text>
                  {invitationsQuery.data.map((invitation) => (
                    <View
                      key={invitation.id}
                      className="rounded-[18px] border border-border bg-card/70 p-4">
                      <View className="flex-row items-start justify-between gap-3">
                        <View className="flex-1 gap-1">
                          <Text className="font-semibold text-foreground">{invitation.email}</Text>
                          <Text variant="small" className="text-muted-foreground">
                            {formatWorkspaceRole(invitation.role)} access
                            {invitation.venues.length > 0
                              ? ` · ${invitation.venues.map((venue) => venue.venue_name).join(', ')}`
                              : ''}
                          </Text>
                        </View>
                        <MetaBadge label="Pending" />
                      </View>
                      {canManageTeamTarget(viewerRole, invitation.role) ? (
                        <Button
                          variant="ghost"
                          className="mt-3"
                          disabled={revokeInvitation.isPending}
                          onPress={() =>
                            handleRevokeInvitation(invitation.id, invitation.email, invitation.role)
                          }>
                          <Icon as={Trash2Icon} size={16} color="white" />
                          <Text className="font-semibold">Revoke invite</Text>
                        </Button>
                      ) : null}
                    </View>
                  ))}
                </View>
              ) : null}
            </Card>

              <Card className="mt-4 gap-4" tone="featured">
                <Text className="text-lg font-semibold text-foreground">Invite a teammate</Text>
                <Text variant="muted" className="leading-6">
                  Send organization access to a manager, social operator, or staff teammate.
                </Text>
                <Input
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="email-address"
                  onChangeText={setInviteEmail}
                  placeholder="teammate@email.com"
                  value={inviteEmail}
                />
                <Tabs value={inviteRole} onValueChange={(value) => setInviteRole(value as typeof inviteRole)}>
                  <TabsList className="w-full">
                    <TabsTrigger value="manager" className="flex-1">
                      <Text>Manager</Text>
                    </TabsTrigger>
                    <TabsTrigger value="social" className="flex-1">
                      <Text>Social</Text>
                    </TabsTrigger>
                    <TabsTrigger value="staff" className="flex-1">
                      <Text>Staff</Text>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
                <Button
                  disabled={createInvitation.isPending || inviteEmail.trim().length === 0}
                  onPress={handleInviteSubmit}>
                  <Text className="font-semibold">
                    {createInvitation.isPending ? 'Sending invite…' : 'Send invite'}
                  </Text>
                </Button>

                {createInvitation.data ? (
                  <Card className="gap-3 border border-border bg-card/70" tone="inset">
                    <Text className="font-semibold text-foreground">{createInvitation.data.email}</Text>
                    <Text variant="muted">
                      {formatWorkspaceRole(createInvitation.data.role)} access · expires{' '}
                      {new Date(createInvitation.data.expires_at).toLocaleString()}
                    </Text>
                  </Card>
                ) : null}

                {createInvitation.error ? (
                  <ErrorState
                    title="Unable to send invite."
                    body={getAPIErrorRecoveryCopy(
                      createInvitation.error,
                      'workspace',
                      'Unable to send invite.'
                    )}
                  />
                ) : null}

                {removeMember.error ? (
                  <ErrorState
                    title="Unable to remove teammate."
                    body={getAPIErrorRecoveryCopy(
                      removeMember.error,
                      'workspace',
                      'Unable to remove teammate.'
                    )}
                  />
                ) : null}

                {revokeInvitation.error ? (
                  <ErrorState
                    title="Unable to revoke invite."
                    body={getAPIErrorRecoveryCopy(
                      revokeInvitation.error,
                      'workspace',
                      'Unable to revoke invite.'
                    )}
                  />
                ) : null}
              </Card>
            </>
            )}
          </>
        ) : null}
      </ScrollView>
    </AppScreen>
  );
}
